import type { RouteDefinition, RouteTable } from "@hakili/app-shell";

import type { BankScreen } from "./shell/BankShell.js";
import type { Persona } from "./shell/personas.js";

/**
 * `D-1020-4`, round `BANK-ROUTES` (`DX-B-012`): **the bank's addresses, as data, on the console's own machinery.**
 *
 * The bank was « not a router, still » (`App.tsx`'s header from round `B` to 20 October): a screen was a
 * selection held in memory, so a reload came back to the landing, a file could not be sent by link, and the
 * back button left the application. 04 § 4.6.4 gives the bank ten paths; the founder's ruling fixed it before
 * the freeze of 8 November.
 *
 * **No second router.** `RouteTable`, `resolveRoute` and `useLocation` are `@hakili/app-shell`'s, the ones
 * `console-web` has run on since round 203. This file is only the bank's TABLE and its two whitelists.
 *
 * **What has an address is what is built.** « Pilotage » and « Profil » have NO address here (« Comité » has one
 * since `COMITE-RECORD-2`, the chef's recording screen, and still no rail entry): an address for a screen that does not exist is a page that answers « n'existe pas »
 * with a straight face. `addresses.spec.ts` holds the rail and this table to each other, so a screen built
 * tomorrow without its address, or an address with no screen, fails there.
 *
 * **A file is named by its REFERENCE** (`/dossiers/CR-2026-012`), the word a banker says on the telephone, not by
 * the uuid the door takes; the reference is read to the id from the lists the server sent HER, so a reference
 * that is not hers is not found , the same answer the door gives.
 *
 * **Danger sentence.** This table decides which SCREEN a persona is shown, never what she may READ. A screen
 * reachable by typing its path is reachable, and the server is what refuses (6.1.8): the chef-only doors answer
 * `403` to an analyst's token whatever this file says (`bank.zz-routes.corridor.spec.ts` asks them). What this
 * file spares her is a screen that would only have shown her that refusal.
 */

/** Parent first: `find` returns the first match, and `navigation()` keeps the first route of a screen. */
const ROUTES: readonly RouteDefinition[] = [
  { path: "/pipeline", screen: "pipeline" },
  { path: "/dossiers", screen: "dossiers" },
  { path: "/dossiers/:reference", screen: "dossier" },
  // `S1-a` COMPORTEMENT DU COMPTE: the tab beside « Dossier » has its own address, standing on the SAME screen as its file (`dossier`), so every read, whitelist and lit rail entry keyed on that screen is unchanged;
  // the tab is read off the path (`comportementOf`). `navigation()` keeps the first route of a screen, so these two add no entry.
  { path: "/dossiers/:reference/comportement", screen: "dossier" },
  { path: "/decisions", screen: "decisions" },
  { path: "/decisions/:reference", screen: "decision" },
  // The chef's page behind the button on the decision (`decision`'s screen, the chef's own).
  { path: "/decisions/:reference/comportement", screen: "decision" },
  // `COMITE-RECORD-2` (D-1024-7): « Passage en comité », the chef's own, reached from « Comités à enregistrer »; no rail entry.
  { path: "/comite/:reference", screen: "comite" },
  { path: "/equipe", screen: "equipe" },
  { path: "/politique", screen: "politique" },
];

/**
 * The screens each persona may be SHOWN: the rail's built entries (`railFor`) plus the screens a link opens.
 * The analyste reaches « Politique » by the composer's « Voir la politique » and reads her read-only copy
 * (03, `DECISION ADDED 12`); she has no rail entry for it, so it is an address she may be shown, not a tab.
 */
export const ECRANS: Readonly<Record<Persona, ReadonlySet<string>>> = {
  analyste: new Set(["pipeline", "dossiers", "dossier", "politique"]),
  // The chef reads a file from the team's pipeline too (`dossier`); the list « Dossiers » is the analyst's.
  chef: new Set(["decisions", "decision", "comite", "pipeline", "dossier", "equipe", "politique"]),
};

/** The persona's table: the same routes for both, so a known-but-forbidden path is told from an unknown one. */
export const tableFor = (persona: Persona): RouteTable => ({
  routes: ROUTES,
  landingScreen: persona === "chef" ? "decisions" : "pipeline",
});

/** A persona's landing path (03 § 3.0: the first of her list). */
export const landingOf = (persona: Persona): string => (persona === "chef" ? "/decisions" : "/pipeline");

/** Who the screen belongs to, for the refusal's sentence. Shared screens are nobody's refusal. */
export const ownerOf = (screen: string): Persona | null => {
  const analyste = ECRANS.analyste.has(screen);
  const chef = ECRANS.chef.has(screen);
  return analyste && chef ? null : analyste ? "analyste" : "chef";
};

/** The address of a list screen, or of a file by its reference. */
export const addressOf = (screen: "pipeline" | "dossiers" | "decisions" | "equipe" | "politique"): string =>
  `/${screen}`;
export const dossierAddress = (reference: string): string => `/dossiers/${encodeURIComponent(reference)}`;
export const decisionAddress = (reference: string): string => `/decisions/${encodeURIComponent(reference)}`;
export const committeeAddress = (reference: string): string => `/comite/${encodeURIComponent(reference)}`;
/** `S1-a`: the behaviour tab of a dossier, and the chef's page behind the decision's button. */
export const dossierComportementAddress = (reference: string): string =>
  `${dossierAddress(reference)}/comportement`;
export const decisionComportementAddress = (reference: string): string =>
  `${decisionAddress(reference)}/comportement`;
/** `S1-a`: is this path a file's behaviour tab? The tab is read off the path; the screen stays its file's. */
export const comportementOf = (path: string): boolean =>
  /^\/(?:dossiers|decisions)\/[^/]+\/comportement$/u.test(path);

/** The shell's own names for the screens a route stands on (the rail lights by these). */
export const shellScreenOf = (screen: string): BankScreen =>
  screen === "dossier" ? "workspace" : (screen as BankScreen);
